import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Box, ButtonBase, InputBase, makeStyles, TextField } from '@material-ui/core';
import _, { isFunction } from 'lodash';
import { useSelector } from 'react-redux';
import DiaLog from '../../../../components/Dialog';
import { i18n } from '../../../../language';
import StyleButton from '../../../../components/StyleButton';
import { DropDownIcon, DropUpIcon, ScanIcon } from '../../../../components/Icon';
import useBarcodeListener from '../../../../utils/useBarcodeListener';
import { useScanPage } from '../../../../utils/useScanQrcode';
import EquipmentAsset from '../../../../components/EquipmentAsset';
import { equipmentAPI, systemAPI } from '../../../../apis';
import useMobile from '../../../../utils/useMobile';
import AutoCompleteInput from '../../../../components/AutoCompleteInput';
import { EQUIPMENT_LENGTH, REMARKS_FIELD } from '../../../../comm/constant';
import { alertMsg } from '../../../../apis/commlogic';
import {
  DWP_COMM_REMARK_BMS_ISFA,
  DWP_COMM_REMARK_BMS_L,
  DWP_COMM_REMARK_BMS_M,
  DWP_COMM_REMARK_BMS_S,
} from '../../../../comm/commandQRcode';
import SingleADSDialog from '../ADS/SingleADSDialog';
import { getUUID, useGetUserInfo } from '../../../../utils';
import verifyHkid, { extractHkid } from '../../../../utils/verifyHkid';
import RedStar from '../../../../components/RedStar';
import { getNewRequestProviderOptions, renderProviderGroup } from '../Util';
import AddRemarks from '../../../../components/AddRemarks';
import useGetRemarksListAPI from '../../hooks/useGetRemarksListAPI';
import Text from '../../../../components/Text';

const useStyles = makeStyles(() => ({
  title: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#7d86a9',
  },
  wardAutoSelect: {
    marginTop: '1rem',
  },
  inputBase: ({ error }) => ({
    border: error ? 'solid 1px #f44336' : 'solid 1px #e4e7eb',
    borderRadius: '10px',
    height: '3.5rem',
    width: '24.5rem',
    padding: '0 1.25rem',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
  }),
  errorMsg: {
    color: '#f44336',
    margin: '0.5rem 0.5rem 0',
    fontFamily: 'NotoSans-Medium',
    fontWeight: '500',
  },
  otherInfoBtn: {
    cursor: 'pointer',
  },
  dropDownButton: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '1.5rem',
    height: '1.5rem',
    marginRight: '1.25rem',
  },
  dropDownIcon: {
    fontSize: '0.938rem',
    color: '#3e3e3e',
  },
  otherInformationTitle: {
    fontSize: '1.125rem',
    color: '#3e3e3e',
  },
  scanButton: {
    marginBottom: '0.375rem',
    flexBasis: '66%',
    marginLeft: '1.5rem',
    marginTop: '1rem',
  },
  scanIcon: {
    color: '#538fff',
  },
  star: {
    color: '#ff0300',
  },
  textField: {
    borderRadius: '0.625rem',
    border: 'solid 1px #e4e7eb',
    padding: '0 1rem',
    '& .MuiInputBase-root': {
      fontFamily: 'NotoSans-Medium',
      fontSize: '1rem',
      color: '#2e4469',
      lineHeight: '1.8rem',
    },
    '& :before': {
      content: 'none',
    },
    '& :after': {
      content: 'none',
    },
  },
  checkBox: {
    '& .MuiCheckbox-root': {
      color: '#c0c6cb',
    },
    '& .MuiTypography-root': {
      fontFamily: 'NotoSans-Medium',
      fontSize: '1rem',
      fontWeight: '500',
      color: '#2e4469',
    },
    '& .Mui-checked': {
      color: '#28d2ab',
    },
  },
  xing: {
    color: 'red',
    marginRight: '0.5rem',
    marginTop: '0.5rem',
  },
  assetItemContain: {
    display: 'flex',
    padding: '0rem 1.42rem',
    width: '100%',
    fontSize: '1.1rem',
    '&:hover': {
      backgroundColor: '#F5FAFF',
    },
    '&:hover $assetItemText': {
      color: '#538FFF',
    },
  },
  assetItemText: {
    width: '33%',
  },
}));

const TitleInput = ({
  title,
  placeholder,
  flexBasis,
  value,
  onChange,
  endAdornment,
  enterHandler,
  onFocus,
  onBlur,
  length = 'unset',
  inputRef,
  error,
  errorMsg,
  id,
  required = false,
}) => {
  const classes = useStyles({ error });
  const inputHandler = (event) => {
    if (isFunction(onChange)) {
      onChange(event.target.value);
    }
  };

  const onKeyDownHandler = (event) => {
    if ((event.code === 'Enter' || event.code === 'NumpadEnter') && isFunction(enterHandler)) {
      enterHandler(event.target.value);
    }
  };

  return (
    <Box display="flex" flexDirection="column" flexBasis={flexBasis}>
      <div className={classes.title}>
        {title}
        {required && <RedStar />}
      </div>
      <InputBase
        id={id}
        className={classes.inputBase}
        placeholder={placeholder}
        style={{ width: '100%', marginTop: '1.063rem' }}
        onChange={inputHandler}
        value={value}
        endAdornment={endAdornment}
        onKeyDown={onKeyDownHandler}
        onFocus={onFocus}
        onBlur={onBlur}
        inputProps={{ maxLength: length }}
        inputRef={inputRef}
      />
      {error && <div className={classes.errorMsg}>{i18n.t(errorMsg)}</div>}
    </Box>
  );
};

const replaceStrBmss = DWP_COMM_REMARK_BMS_S;
const replaceStrBmsm = DWP_COMM_REMARK_BMS_M;
const replaceStrBmsl = DWP_COMM_REMARK_BMS_L;
const replaceStrBmsIsfa = DWP_COMM_REMARK_BMS_ISFA;

const emptyEquipment = {
  category: '',
  categoryName: '',
  availableQty: 0,
  assets: [],
  loanQty: 1,
};

const replaceHandler = (oldReplaceValue, newReplaceValue, value, enter, temp) => {
  const reg = new RegExp(oldReplaceValue, 'g');
  let str;
  if (value.target) {
    str = value.target.value.replace(reg, newReplaceValue);
  } else if (enter) {
    str = `${temp.remarks + value.replace(reg, newReplaceValue)}\n`;
  } else {
    str = `${temp.remarks + value.replace(reg, newReplaceValue)}`;
  }

  return str;
};

const judgeConstantRemark = (val, constantRemark) => val.indexOf(constantRemark) !== -1;

const handleInputRemark = ({ value, setFormData, enter }) => {
  setFormData((preData) => {
    let temp = { ...preData };
    let val = value.target ? value.target.value : value;

    if (judgeConstantRemark(val, replaceStrBmss)) {
      val = replaceHandler(replaceStrBmss, REMARKS_FIELD.BIPAP_MASK_SIZE_S, value, enter, temp);
    } else if (judgeConstantRemark(val, replaceStrBmsm)) {
      val = replaceHandler(replaceStrBmsm, REMARKS_FIELD.BIPAP_MASK_SIZE_M, value, enter, temp);
    } else if (judgeConstantRemark(val, replaceStrBmsl)) {
      val = replaceHandler(replaceStrBmsl, REMARKS_FIELD.BIPAP_MASK_SIZE_L, value, enter, temp);
    } else if (judgeConstantRemark(val, replaceStrBmsIsfa)) {
      val = replaceHandler(replaceStrBmsIsfa, REMARKS_FIELD.EXCEPT_ISOLATION_WARD, value, enter, temp);
    } else if (val.indexOf(REMARKS_FIELD.START_CODE) !== -1 && val.indexOf(REMARKS_FIELD.END_CODE) !== -1) {
      if (enter) {
        val = temp.remarks + val.replace(REMARKS_FIELD.REPLACE_REMARK_REGEX, (value) => `${value.slice(13, -12)}\n`);
      } else {
        val = value.target
          ? val.replace(REMARKS_FIELD.REPLACE_REMARK_REGEX, (value) => value.slice(13, -12))
          : temp.remarks + val.replace(REMARKS_FIELD.REPLACE_REMARK_REGEX, (value) => value.slice(13, -12));
      }
    }

    temp.remarks = val;
    return { ...temp };
  });
};

const handleRemoveCategory =
  ({ setEquipmentAssets }) =>
  (index) => {
    if (index === 0) {
      setEquipmentAssets((preData) => {
        return preData.length === 1 ? [{ ...emptyEquipment, assets: [] }] : [...preData.slice(1)];
      });
    } else {
      setEquipmentAssets((preData) => [...preData.slice(0, index), ...preData.slice(index + 1)]);
    }
  };

const handleAddAsset =
  ({ setEquipmentAssets }) =>
  ({ categoryId, model, assetId, localCode, categoryName, status, remark }) =>
    setEquipmentAssets((preData) => {
      let tempData = [...preData];
      let newAsset = {
        assetId,
        localCode,
        model,
        status,
        remark,
      };

      // 已存在對應的category
      if (_.some(tempData, (item) => item.category === categoryId)) {
        let index = _.findIndex(tempData, (item) => item.category === categoryId);

        // 存在對應的 assetId
        if (_.some(tempData[index].assets, (item) => item.assetId === assetId)) {
          alertMsg(i18n.t('EQUIPMENT_VIEWREQUEST_ASSET_ERROR_MSG'), 'assetId', 'warning');
          return tempData;
        } else {
          // 不存在對應的 assetId
          let loanQty = tempData[index].loanQty;
          let assets = tempData[index].assets;
          if (assets.length < loanQty) {
            tempData[index].assets.push(newAsset);
          } else {
            tempData[index].loanQty += 1;
            tempData[index].assets.push(newAsset);
          }
        }
      }
      // 不存在對應的category，但是有一個 category tab 為空
      else if (_.some(tempData, (item) => item.category === '')) {
        let emptyIndex = tempData.length - 1;

        tempData.splice(emptyIndex, 1, {
          ...preData[emptyIndex],
          category: categoryId,
          categoryName,
          assets: [newAsset],
        });
      }
      // 不存在對應的category，並且沒有 category tab 為空
      else {
        let newEquipment = {
          availableQty: 0,
          loanQty: 1,
          category: categoryId,
          categoryName,
          assets: [newAsset],
        };
        tempData.push(newEquipment);
      }

      return tempData;
    });

const NewLoanRequestDialog = ({ dialogCloseHandler, dialogFinishHandler }) => {
  const initFormData = {
    ward: '',
    provider: '',
    patient: '',
    bedID: '',
    remarks: '',
    // loanReminderDuration: 14,
  };
  const classes = useStyles();
  const inputRef = useRef(null);
  const [formData, setFormData] = useState(initFormData);
  const [otherInformationOpen, setOtherInformationOpen] = useState(false);
  const loginInfo = useSelector((state) => state.loginInfo);
  const [providerList, setProviderList] = useState([]);
  const [wardList, setWardList] = useState({});
  const [assetList, setAssetList] = useState([]);
  const [equipmentAssets, setEquipmentAssets] = useState([emptyEquipment]);
  const [barcodeOpen, setBarcodeOpen] = useState(true);
  const [flag, setFlag] = useState(0);
  const [assetId, setAssetId] = useState('');
  const [isPad] = useMobile();
  const assetRef = useRef(null);
  const [errors] = useState({ patient: '' });
  const [adsPopup, setAdsPopup] = useState(false);
  const userInfo = useGetUserInfo();
  const adsRef = useRef(null);
  const [loanUniqueKey] = useState(getUUID);
  const [selectedCategoryWards, setSelectedCategoryWards] = useState([]);

  const requiredPatientHN = useMemo(() => {
    return _.some(selectedCategoryWards, (item) => String(item?.isPatientNo) === '1');
  }, [selectedCategoryWards]);

  // provider options
  const providersOptions = useMemo(() => {
    const params = {
      type: 'lend',
      equipmentAssets,
      flag,
      providerList,
      currentWardId: loginInfo.wardId,
    };
    return getNewRequestProviderOptions(params);
  }, [providerList, loginInfo.wardId, flag, equipmentAssets]);

  const getCanAddAsset = (categorySatelliteStoresId) => {
    // 拿到当前provider所有satellite store的所有设备
    const allSatellites = _.map(currentProvider?.categorys, (categoryItem) => categoryItem?.satelliteStores).flat();
    const isFloor = currentProvider?.isFloor ?? false;
    const hasEquipment = _.some(allSatellites, (item) => item?.categorySatelliteStoresId === categorySatelliteStoresId);
    if (isFloor && categorySatelliteStoresId === null) {
      // 当前选了楼层，并且设备没有被分配到房间时可以添加
      return true;
    } else if (!isFloor && hasEquipment) {
      // 当前选了房间，并且设备被分配到当前房间可以添加
      return true;
    } else {
      return false;
    }
  };

  // 獲取 equipment API
  // const getEquipment = async (assetId) => {
  //   try {
  //     // 如果沒有 assetId 和 provider， 則不 call API
  //     if (!formData.provider || !assetId) {
  //       return;
  //     }
  //     // 把用 ; 号隔开的 asset Id 变成一个数组
  //     const assetIdList = assetId.split(';');

  //     _.forEach(assetIdList, async (item) => {
  //       const { result } = await equipmentAPI.getEquipment({ assetId: item, provideWardId });
  //       // 只有当前所选的provider下面的category的 satellite store 有查询的这个设备时才能添加
  //       if (getCanAddAsset(result.categorySatelliteStoresId)) {
  //         equipmentAssetsHandler({ type: 'addAsset' })(result);
  //       } else {
  //         alertMsg(
  //           i18n.t('EQUIPMENT_ADD_DIFFERENT_SATELLITE_STORE_ASSET_ERROR', { value: provideWardName }),
  //           'error',
  //           'error'
  //         );
  //       }
  //     });
  //     setAssetId('');
  //   } catch (error) {
  //     setAssetId('');
  //     console.log(error);
  //   }
  // };

  const otherInformationController = () => setOtherInformationOpen((open) => !open);

  // 處理 assets 相關操作
  const equipmentAssetsHandler = useCallback(({ type, flag }) => {
    switch (type) {
      case 'addEquipment':
        setEquipmentAssets((preData) => {
          return [...preData, { ...emptyEquipment, assets: [] }];
        });
        return;
      case 'changeCategory':
        return (value, categoryName, wardIds, isSingleEquipment) =>
          setEquipmentAssets((preData) => {
            let tempData = [...preData];

            tempData.splice(flag, 1, {
              ...preData[flag],
              category: value,
              categoryName,
            });
            return tempData;
          });
      case 'removeCategory':
        return handleRemoveCategory({ setEquipmentAssets });
      case 'addAsset':
        return handleAddAsset({ setEquipmentAssets });
      case 'removeAsset':
        return (id) =>
          setEquipmentAssets((preData) => {
            let tempData = [...preData];
            tempData[flag].assets = _.filter(tempData[flag].assets, (item) => item.assetId !== id);
            return tempData;
          });
      case 'updateAvailableQty':
        return (newAvailableQty) =>
          setEquipmentAssets((preData) => {
            let tempData = [...preData];

            tempData.splice(flag, 1, {
              ...preData[flag],
              availableQty: newAvailableQty,
            });
            return tempData;
          });
      case 'updateLoanQty':
        return (newLoanQty) =>
          setEquipmentAssets((preData) => {
            let tempData = [...preData];
            let assets = tempData[flag].assets;

            if (newLoanQty < assets.length) {
              return tempData;
            }

            tempData.splice(flag, 1, {
              ...preData[flag],
              loanQty: newLoanQty,
            });
            return tempData;
          });
      default:
        return;
    }
  }, []);

  // 處理 assets 以外的輸入
  const formChangeHandler = useCallback(
    (key, enter) => (value) => {
      if (key === 'provider') {
        setEquipmentAssets((preData) => {
          return _.map(preData, (assetItem, index) => {
            if (index === flag) {
              return { ...assetItem, assets: [] };
            }
            return assetItem;
          });
        });
      }
      if (key === 'ward') {
        assetRef?.current?.focus();
      }
      if (key === 'remarks') {
        handleInputRemark({ value, setFormData, enter });
        return;
      }
      if (key === 'patient') {
        setFormData((preData) => {
          let newValue = value;
          newValue = newValue.toUpperCase();
          newValue = newValue.replace(/[\(\)]+/g, '');

          return { ...preData, [key]: newValue };
        });
      } else {
        setFormData((preData) => ({ ...preData, [key]: value }));
      }
    },
    []
  );
  // barcode 槍的事件
  const barcodeEvent = async (e) => {
    if (e.replace(/\s*/g, '').toUpperCase().startsWith('HN')) {
      formChangeHandler('patient')(e.replace(/\s*/g, ''));
      setOtherInformationOpen(true);
      setAssetId('');
    } else if (
      e.indexOf(replaceStrBmss) !== -1 ||
      e.indexOf(replaceStrBmsm) !== -1 ||
      e.indexOf(replaceStrBmsl) !== -1 ||
      e.indexOf(replaceStrBmsIsfa) !== -1 ||
      (e.indexOf(REMARKS_FIELD.START_CODE) !== -1 && e.indexOf(REMARKS_FIELD.END_CODE) !== -1)
    ) {
      setOtherInformationOpen(true);
      inputRef.current.focus();
      formChangeHandler('remarks')(e);
    } else if (e.toUpperCase().startsWith('WB ')) {
      handleScanHkid(formChangeHandler('patient'), formChangeHandler('bedID'), e);
      setOtherInformationOpen(true);
    } else {
      handleSelectAsset(e);
    }
  };

  useBarcodeListener(barcodeEvent, barcodeOpen);

  // 獲取 provider list
  const getProvider = useCallback(async () => {
    try {
      const { result } = await equipmentAPI.getProvider({ hospitalId: loginInfo.hospitalId, wardId: loginInfo.wardId });
      setProviderList(result.provides);
      formChangeHandler('provider')(loginInfo.wardId);
    } catch (error) {
      console.log(error);
    }
  }, [loginInfo]);

  // 獲取 ward list
  const getWardList = useCallback(async () => {
    try {
      const { result } = await systemAPI.getSysHospitalWardList({ hospitalId: loginInfo.hospitalId, wardId: null });
      setWardList(result?.[0]);
    } catch (error) {
      console.log(error);
    }
  }, [loginInfo]);

  useEffect(() => {
    (async () => {
      await getProvider();
    })();
    (async () => {
      await getWardList();
    })();
  }, []);

  // ward optionss
  const wardsOptions = useMemo(() => {
    let wards = [];

    if (Array.isArray(wardList?.wards)) {
      wards = _.map(wardList?.wards || [], (item) => {
        const showWardOption = item?.enableYn === 0;
        let roomOptions = [];
        const wardOption = {
          text: `${wardList.hospitalCode} - ${item.wardName}`,
          value: item.wardId,
          wardId: item.wardId,
          adsWardLocationId: null,
          tagId: item.wardId,
        };
        const hasRoomType = _.some(item.wardLocation, (locationItem) => locationItem.adsRoomType === 'R');
        if (hasRoomType) {
          roomOptions = _.filter(item.wardLocation, (locationItem) => locationItem.adsRoomType === 'R').map(
            (locationItem) => ({
              text: `${wardList.hospitalCode} - ${item.wardName} - ${locationItem.roomName}`,
              value: `${item.wardId} ${locationItem.adsWardLocationId}`,
              wardId: item.wardId,
              adsWardLocationId: locationItem.adsWardLocationId,
              tagId: locationItem.adsWardLocationId,
            })
          );
        }
        return showWardOption ? [wardOption, ...roomOptions] : [...roomOptions];
      }).flat();
    }

    return wards;
  }, [wardList, formChangeHandler, formData.provider]);

  // 当前选择的wardId
  const wardId = wardsOptions.find((item) => item.value === formData.ward)?.wardId;
  // 当前选择的 adsWardLocationId
  const adsWardLocationId = wardsOptions.find((item) => item.value === formData.ward)?.adsWardLocationId;
  // 当前选择的 providerWardId
  const provideWardId = providersOptions.find((item) => item.value === formData.provider)?.wardId;
  // 当前选择的provider
  const currentProvider = useMemo(
    () => _.find(providersOptions, (item) => item?.value === formData?.provider),
    [formData.provider, providersOptions]
  );
  // 当前选择的 categorySatelliteStoreId
  const categorySatelliteStoresId = useMemo(() => {
    if (!currentProvider || currentProvider?.isFloor) {
      // 如果选的是楼层 categorySatelliteStoresId 为null
      return null;
    } else {
      const selectedCategory = equipmentAssets?.[0]?.category;
      const currentStore =
        _.find(currentProvider?.categorys, (item) => item?.id === selectedCategory)?.satelliteStores ?? [];
      return currentStore?.[0]?.categorySatelliteStoresId ?? null;
    }
  }, [equipmentAssets?.[0]?.category, currentProvider]);
  const provideWardName = providersOptions.find((item) => item.value === formData.provider)?.provideWardName;

  let submitLock = false;
  // new loan request 的 API
  const newLoanRequestAPI = async ({ adsCategoryId, isAdsServer }) => {
    try {
      if (submitLock) {
        return;
      }
      submitLock = true;
      let params = {
        bedNo: formData.bedID,
        loanCategorys: _.map(
          _.filter(equipmentAssets, (item) => Boolean(item.category)),
          (item) => ({
            categoryId: item.category,
            categoryName: item.categoryName,
            assetCount: item.loanQty,
            assetIds: _.map(item.assets, (assetItem) => assetItem.assetId),
          })
        ),
        patientNo: formData.patient,
        provideWardId,
        categorySatelliteStoresId,
        remark: formData.remarks,
        wardId,
        adsWardLocationId,
        // loanReminderDuration: formData.loanReminderDuration,
        provideWardName,
        wardName: loginInfo.wardName,
        model: 0,
        adsCategoryId,
        currentWard: loginInfo.wardId,
        instCode: userInfo.hospCode,
        isAdsServer,
        loanType: 'BORROW',
        loanUniqueKey,
      };

      await equipmentAPI.newLoanRequset(params);
      submitLock = false;
      dialogFinishHandler();
    } catch (error) {
      submitLock = false;
      if (error.code === 'ECONNABORTED') {
        dialogFinishHandler();
      }
      console.log(error);
    }
  };

  // 提交表單事件
  const dialogOpenHandler = useCallback(() => {
    try {
      setAdsPopup(true);
      adsRef?.current?.getAdsLocation?.();
    } catch (error) {
      console.log(error);
    }
  }, []);

  // assetId input 獲取/失去焦點
  const barcodeControl = useCallback(
    (props) => () => {
      setBarcodeOpen(props);
    },
    []
  );

  // 根據 provider 限制可以選擇的 category
  const provideCategorys = useMemo(() => {
    let categorys = [];

    if (formData.provider) {
      categorys = _.map(currentProvider?.categorys, (item) => item.id);
    }

    return categorys;
  }, [providerList, formData.provider]);

  // 选择asset
  const handleSelectAsset = (assetId) => {
    if (!assetId) return;

    // 把用 ; 号隔开的 asset Id 变成一个数组
    const assetIdList = assetId.split(';');
    _.forEach(assetIdList, (item) => {
      const selectedAsset = _.find(assetList, (assetItem) => assetItem?.value === item);
      let newAsset = {
        categoryId: selectedAsset?.categoryId,
        assetId: selectedAsset?.assetId,
        localCode: selectedAsset?.refNo,
        model: selectedAsset?.model,
        status: selectedAsset?.status,
        remark: selectedAsset?.remark,
        categoryName: selectedAsset?.categoryName,
        categorySatelliteStoresId: selectedAsset?.categorySatelliteStoresId,
      };
      // 只有当前所选的provider下面的category的 satellite store 有查询的这个设备时才能添加
      if (getCanAddAsset(newAsset?.categorySatelliteStoresId)) {
        equipmentAssetsHandler({ type: 'addAsset' })(newAsset);
      } else {
        alertMsg(
          i18n.t('EQUIPMENT_ADD_DIFFERENT_SATELLITE_STORE_ASSET_ERROR', { value: provideWardName }),
          'error',
          'error'
        );
      }
    });
    setAssetId('');
  };

  // Scan Equipment 的組件和調用函數,返回第一個是組件，第二個是開啟的函數
  const [ScanPortal, openScanPage] = useScanPage(handleSelectAsset);
  const [ScanPortalHN, openHNScanPage] = useScanPage(
    handleScanPatientQrCode(formChangeHandler('patient'), formChangeHandler('bedID'))
  );
  const [ScanPortalRemarks, openScanPageRemarks] = useScanPage(formChangeHandler('remarks', true));
  // provider no option 时，点击autocomplete时的事件
  const providerOnOpen = useCallback(async () => {
    try {
      if (providersOptions.length === 0) {
        await getProvider();
      }
    } catch (error) {
      console.log(error);
    }
  }, [getProvider, providersOptions]);

  // request ward no option时，点击autocomplete时的事件
  const wardOnOpen = useCallback(async () => {
    try {
      if (wardsOptions.length === 0) {
        await getWardList();
      }
    } catch (error) {
      console.log(error);
    }
  }, [getWardList, wardsOptions]);

  // confirm button disable
  const confirmDisable = useMemo(
    () =>
      !(formData.provider && formData.ward) ||
      _.some(equipmentAssets, (item) => item.categoryName === '') ||
      adsPopup ||
      (requiredPatientHN && !formData.patient),
    [formData.provider, formData.ward, errors.patient, equipmentAssets, adsPopup, requiredPatientHN, formData.patient]
  );

  const getBedNo = async () => {
    try {
      if (formData.patient.replace(/\s*/g, '').startsWith('HN')) {
        const { result } = await equipmentAPI.getBedNo({
          patientNo: formData.patient.replace(/\s*/g, ''),
          hkid: '',
        });
        formChangeHandler('bedID')(result.BedNo);
        formChangeHandler('patient')(formData.patient.replace(/\s*/g, ''));
      } else if (formData.patient.toUpperCase().startsWith('WB ')) {
        handleScanHkid(formChangeHandler('patient'), formChangeHandler('bedID'), formData.patient);
      } else {
        handleSelectAsset(formData.patient);
        formChangeHandler('patient')('');
      }
    } catch (error) {
      console.log(error);
    }
  };

  const adsWardInfo = useMemo(() => {
    let provider = '';
    let request = '';

    if (formData.ward) {
      request = _.find(wardsOptions, (item) => item.value === formData.ward)?.text ?? '';
    }

    if (formData.provider) {
      provider = _.find(providersOptions, (item) => item.value === formData.provider)?.provideWardName ?? '';
    }

    return {
      provider,
      request,
    };
  }, [formData.ward, formData.provider, wardsOptions, providersOptions]);

  // 获取 equipment asset list
  const getEquipmentAsset = async (params) => {
    try {
      const { result } = await equipmentAPI.getEquipmentAssetList(params);
      const assetList = _.map(result, (assetItem) => {
        const model = assetItem?.model ?? '-';
        const localCode = assetItem?.refNo ?? '-';
        return {
          ...assetItem,
          text: `${assetItem?.assetId}   ${localCode}   ${model}`,
          value: assetItem?.assetId,
          group: `${assetItem?.categoryId}-${assetItem?.categoryName}`, // 用于下拉分组
        };
      });
      setAssetList(assetList);
    } catch (error) {
      console.log('get asset error: ', error);
    }
  };

  // 分组下的asset item
  const ListItem = useCallback(
    ({ item }) => {
      const selectedAsset = equipmentAssets?.[flag]?.assets;
      const isSelected = _.some(selectedAsset, (asset) => asset.assetId === item?.assetId);

      return (
        <div className={classes.assetItemContain} style={{ color: isSelected ? '#2E4469' : '#7D86A9' }}>
          <Box className={classes.assetItemText}>{item?.assetId}</Box>
          <Box className={classes.assetItemText}>{item?.refNo ? item?.refNo : '-'}</Box>
          <Box className={classes.assetItemText}>{item?.model ? item?.model : '-'}</Box>
        </div>
      );
    },
    [equipmentAssets, flag]
  );
  const GroupHeader = useCallback(
    ({ categoryName }) => {
      const isSelectedCategory = equipmentAssets?.[flag]?.category !== '';

      return (
        <Box
          style={{
            color: '#2E4469',
            paddingLeft: 10,
            paddingTop: 12,
            paddingBottom: 8,
          }}
        >
          <Text fontWeight="400" fontSize="1rem">{`${
            !isSelectedCategory ? categoryName : ''
          } (Asset ID / Local Code / Model)`}</Text>
        </Box>
      );
    },
    [equipmentAssets?.[flag]?.category]
  );
  const assetRenderGroup = (params) => {
    const categoryName = params?.group?.split('-')?.[1] ?? '';
    return (
      <li key={params.key}>
        {params.group && <GroupHeader categoryName={categoryName} />}
        <ul style={{ paddingLeft: 0 }}>{params.children}</ul>
      </li>
    );
  };

  const { remarkList: activeRemarkList, getRemarksListAPI } = useGetRemarksListAPI();

  // 选择remark
  const handleSelectRemark = (remarks) => {
    setFormData((o = {}) => ({
      ...o,
      remarks: `${o?.remarks || ''} ${remarks}`.slice(0, 99),
    }));
  };

  useEffect(() => {
    // 等所需的数据call完回来再call,以防call多次的情况
    if (providersOptions.length !== 0 && formData?.provider) {
      const currentProvider = _.find(providersOptions, (item) => item?.value === formData?.provider);
      const satelliteStoresIds = _.map(currentProvider?.categorys, (item) => item?.categorySatelliteStoresId);
      const params = {
        wardId: loginInfo.wardId,
        satelliteStoresIds: currentProvider?.isFloor ? [] : satelliteStoresIds,
        categoryIds: equipmentAssets?.[flag]?.category ? [equipmentAssets?.[flag]?.category] : [],
      };

      (async () => await getEquipmentAsset(params))();
    }
  }, [formData?.provider, loginInfo.wardId, equipmentAssets?.[flag]?.category]);

  useEffect(() => {
    if (assetId) {
      handleSelectAsset(assetId);
    }
  }, [assetId]);

  useEffect(() => {
    if (equipmentAssets?.[flag]?.category) {
      getRemarksListAPI({ hospitalCategoryIds: [equipmentAssets?.[flag]?.category] });
    }
  }, [equipmentAssets?.[flag]?.category]);

  return (
    <>
      <ScanPortal />
      <ScanPortalHN />
      <ScanPortalRemarks />
      <DiaLog
        open={true}
        handleClose={dialogCloseHandler}
        handleCancel={dialogCloseHandler}
        handleConfirm={dialogOpenHandler}
        title={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_TITLE')}
        cancel={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_CANCEL')}
        confirm={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_CONFIRM')}
        dialogfootStyle={{ marginTop: '1rem' }}
        disabled={confirmDisable}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          pb="1.313rem"
          borderBottom="solid 1px #e6e7e8"
        >
          <Box>
            <div className={classes.title}>
              {i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_REQUEST_WARD')} <span className={classes.star}>*</span>
            </div>
            <AutoCompleteInput
              className={classes.wardAutoSelect}
              id="ViewRequest_autoSelect_ward"
              width="19.375rem"
              height="3.5rem"
              itemList={wardsOptions}
              placeholder={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_REQUEST_WARD_PLACEHOLDER')}
              value={formData.ward}
              onChange={formChangeHandler('ward')}
              onOpen={wardOnOpen}
            />
          </Box>
          <Box>
            <div className={classes.title}>
              {i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_PROVIDER')} <span className={classes.star}>*</span>
            </div>
            <AutoCompleteInput
              className={classes.wardAutoSelect}
              id="ViewRequest_autoSelect_provider"
              width="19.375rem"
              height="3.5rem"
              itemList={providersOptions}
              placeholder={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_PROVIDER_PLACEHOLDER')}
              value={formData.provider}
              onChange={formChangeHandler('provider')}
              onOpen={providerOnOpen}
              whiteSpace
              renderGroup={(params) => renderProviderGroup(params, equipmentAssets, flag, providersOptions)}
            />
          </Box>
        </Box>
        <Box display="flex" flexDirection="column" pt="1.25rem">
          <Box mb="1.313rem" display="flex" justifyContent="space-between" alignItems="flex-end">
            <Box flexBasis="100%">
              <div className={classes.title} style={{ marginBottom: '1.0rem' }}>
                {i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_ASSET_ID')}
              </div>
              <AutoCompleteInput
                id="ViewRequest-input-asset"
                placeholder={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_ASSET_ID_PLACEHOLDER')}
                flexBasis="100%"
                onFocus={barcodeControl(false)}
                onBlur={barcodeControl(true)}
                value={null}
                onChange={(val, clearInputValueFun) => {
                  setAssetId(val);
                  if (_.isFunction(clearInputValueFun)) {
                    clearInputValueFun();
                  }
                }}
                inputRef={assetRef}
                itemList={assetList}
                renderGroup={assetRenderGroup}
                renderOption={(props) => <ListItem item={props} />}
                disabledSelectionAutoCompletion={true}
                whiteSpace
              />
            </Box>
            {isPad && (
              <StyleButton
                id="ViewRequest-button-scan-asset"
                className={classes.scanButton}
                width="14.125rem"
                text={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_SCAN_EQUIPMENT')}
                Icon={ScanIcon}
                iconStyle={{ fontSize: '1.5rem', marginRight: '0.75rem' }}
                onClick={openScanPage}
              />
            )}
          </Box>
          <EquipmentAsset
            data={equipmentAssets}
            flag={flag}
            setFlag={setFlag}
            changeDataHandler={equipmentAssetsHandler}
            provider={provideWardId}
            isFloorProvider={currentProvider?.isFloor ?? false}
            provideCategorys={provideCategorys}
            controller={{
              assetId: true,
              category: true,
              loanQty: true,
              asset: true,
              Decline: true,
            }}
            newRequest={true}
            setSelectedCategoryWards={setSelectedCategoryWards}
            categorySatelliteStoresId={categorySatelliteStoresId}
          />
          {/* <LongLoanReminder value={formData.loanReminderDuration} onChange={formChangeHandler('loanReminderDuration')} /> */}
        </Box>
        <Box
          id="ViewRequest-button-otherinformation"
          className={classes.otherInfoBtn}
          display="flex"
          p="1.125rem 0.813rem"
          borderTop="solid 1px #e6e7e8"
          borderBottom="solid 1px #e6e7e8"
          onClick={otherInformationController}
        >
          <div className={classes.dropDownButton}>
            {otherInformationOpen ? (
              <DropUpIcon className={classes.dropDownIcon} />
            ) : (
              <DropDownIcon className={classes.dropDownIcon} />
            )}
          </div>
          <div className={classes.otherInformationTitle}>
            {i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_OTHER_INFORMATION')}
          </div>
        </Box>
        {otherInformationOpen && (
          <Box display="flex" flexDirection="column" p="1rem 0 1.5rem" borderBottom="solid 1px #e6e7e8">
            <Box display="flex" justifyContent="space-between" mb="1.25rem">
              <TitleInput
                id="ViewRequest-input-patient"
                title={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_PATIENT_HN')}
                placeholder={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_PATIENT_HN_PLACEHOLDER')}
                flexBasis="48%"
                endAdornment={
                  isPad ? (
                    <ButtonBase
                      id="ViewRequest-button-scan-patient"
                      onClick={() => {
                        (async () => {
                          await openHNScanPage();
                        })();
                      }}
                    >
                      <ScanIcon className={classes.scanIcon} />
                    </ButtonBase>
                  ) : (
                    <div />
                  )
                }
                value={formData.patient}
                onChange={formChangeHandler('patient')}
                length={EQUIPMENT_LENGTH.REQUEST_HN}
                enterHandler={getBedNo}
                onFocus={barcodeControl(false)}
                onBlur={() => {
                  barcodeControl(true)();
                  setFormData((preData) => ({ ...preData, patient: preData.patient.replace(/\s*/g, '') }));
                }}
                error={Boolean(errors.patient)}
                errorMsg={errors.patient}
                required={requiredPatientHN}
              />
              <TitleInput
                id="ViewRequest-input-bed"
                title={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_BED_ID')}
                placeholder={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_BED_ID_PLACEHOLDER')}
                flexBasis="48%"
                value={formData.bedID}
                onChange={formChangeHandler('bedID')}
                length={EQUIPMENT_LENGTH.REQUEST_BED}
              />
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              flexDirection="column"
              // alignItems="flex-end"
              mb="1.313rem"
            >
              {/* <TitleInput
                id="ViewRequest-input-remark"
                title={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_REMARK')}
                placeholder={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_REMARK_PLACEHOLDER')}
                value={formData.remarks}
                onChange={formChangeHandler('remarks')}
                length={EQUIPMENT_LENGTH.REQUEST_REMARK}
                flexBasis="100%"
                rows={3}
              /> */}
              <Box display="flex" justifyContent="space-between">
                <div className={classes.title}>{i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_REMARK')}</div>
                <AddRemarks list={activeRemarkList} handleSelect={handleSelectRemark} />
              </Box>
              <Box display="flex">
                <TextField
                  id="ViewRequest-input-remark"
                  className={classes.textField}
                  placeholder={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_REMARK_PLACEHOLDER')}
                  multiline={true}
                  fullWidth={true}
                  rows={4}
                  value={formData.remarks}
                  onChange={formChangeHandler('remarks')}
                  style={{ marginTop: '1rem' }}
                  inputRef={inputRef}
                  onFocus={() => setBarcodeOpen(false)}
                  onBlur={() => setBarcodeOpen(true)}
                />
                {isPad && (
                  <StyleButton
                    id="ViewRequest-button-scan-remark"
                    className={classes.scanButton}
                    width="14.125rem"
                    text={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_SCAN_EQUIPMENT')}
                    Icon={ScanIcon}
                    iconStyle={{ fontSize: '1.5rem', marginRight: '0.75rem' }}
                    onClick={openScanPageRemarks}
                  />
                )}
              </Box>
            </Box>
          </Box>
        )}
      </DiaLog>
      <SingleADSDialog
        handleNewRequest={newLoanRequestAPI}
        equipmentAssets={equipmentAssets}
        formData={formData}
        adsPopup={adsPopup}
        handleClose={() => setAdsPopup(false)}
        adsType="new"
        adsWardInfo={adsWardInfo}
        ref={adsRef}
        provideWardId={provideWardId}
        requestWard={wardId}
        adsWardLocationId={adsWardLocationId}
        categorySatelliteStoresId={categorySatelliteStoresId}
      />
    </>
  );
};

export const handleScanPatientQrCode = (updatePatient, updateBedId) => async (value) => {
  try {
    if (value.toUpperCase().replace(/\s*/g, '').startsWith('HN')) {
      updatePatient?.(value);
    } else if (value.toUpperCase().startsWith('WB ')) {
      handleScanHkid(updatePatient, updateBedId, value);
    }
  } catch (error) {
    console.log(error);
  }
};

export const handleScanHkid = async (updatePatient, updateBedId, value) => {
  try {
    const parts = value.split(' ');
    const hkid = extractHkid(parts[1]);
    // 验证hkid是否符合要求
    if (verifyHkid(hkid)) {
      const { result } = await equipmentAPI.getBedNo({
        hkid: hkid,
        patientNo: '',
      });
      updateBedId?.(result?.BedNo ?? '');
      updatePatient?.(result?.PatientNo ?? '');
    }
  } catch (error) {
    console.log(error);
    updatePatient?.('');
  }
};

export default NewLoanRequestDialog;
